import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Cake, Gift, ChevronDown } from 'lucide-react';
import { MaterialCard } from '../dashboard-material/layout/MaterialDashboardGrid';

interface Birthday {
  id: string;
  firstName: string;
  lastName: string;
  type: 'student' | 'staff';
  date: string;
}

interface BirthdayCardProps {
  birthdays: Birthday[];
}

export const BirthdayCard: React.FC<BirthdayCardProps> = ({ birthdays }) => {
  // Ensure we always have an array to work with
  const safeBirthdays = birthdays || [];
  
  // Auto-collapse state: collapse if no birthdays, expand if there are birthdays
  const [isCollapsed, setIsCollapsed] = useState(true);

  // Bulletproof YMD string function
  function getYMDString(date) {
    // If it's already a YMD string, return as-is
    if (typeof date === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(date)) return date;
    const d = new Date(date);
    return (
      d.getFullYear() +
      '-' +
      String(d.getMonth() + 1).padStart(2, '0') +
      '-' +
      String(d.getDate()).padStart(2, '0')
    );
  }

  const todayStr = getYMDString(new Date());
  
  const todayBirthdays = safeBirthdays.filter(b => getYMDString(b.date) === todayStr);
  const upcomingBirthdays = safeBirthdays.filter(b => {
    const bYMD = getYMDString(b.date);
    const bDate = new Date(bYMD + "T00:00:00");
    const tDate = new Date(todayStr + "T00:00:00");
    const diff = Math.round((bDate - tDate) / (1000 * 60 * 60 * 24));
    return diff > 0 && diff <= 7;
  });

  const totalBirthdays = todayBirthdays.length + upcomingBirthdays.length;
  const hasBirthdays = totalBirthdays > 0;

  // Auto-collapse/expand based on data
  useEffect(() => {
    setIsCollapsed(!hasBirthdays);
  }, [hasBirthdays]);

  const toggleCollapsed = () => {
    setIsCollapsed(!isCollapsed);
  };

  // Always render the card - never return null!
  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className="mb-6"
    >
      <MaterialCard className="overflow-hidden">
        {/* Collapsible Header */}
        <button
          onClick={toggleCollapsed}
          className="w-full p-4 flex items-center justify-between hover:bg-gray-50 transition-colors duration-150"
          {...(!isCollapsed ? { 'aria-expanded': 'true' } : { 'aria-expanded': 'false' })}
        >
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-pink-500 flex items-center justify-center flex-shrink-0">
              <Cake className="w-5 h-5 text-white" />
            </div>
            <div className="text-left">
              <h3 className="font-medium text-gray-900 text-base leading-6">
                Upcoming Birthdays
              </h3>
              <p className="text-gray-600 mt-0.5 text-xs leading-4">
                Celebrate your hive members
              </p>
            </div>
            <div className="ml-6">
              <span className={`text-3xl font-bold ${
                hasBirthdays ? 'text-pink-500' : 'text-gray-400'
              }`}>
                {totalBirthdays}
              </span>
            </div>
          </div>
          <ChevronDown 
            className={`w-5 h-5 text-gray-400 transition-transform duration-200 ${
              isCollapsed ? 'rotate-180' : 'rotate-0'
            }`}
          />
        </button>

        {/* Collapsible Content */}
        <motion.div
          initial={false}
          animate={{
            height: isCollapsed ? 0 : 'auto',
            opacity: isCollapsed ? 0 : 1
          }}
          transition={{
            duration: 0.25,
            ease: 'easeInOut'
          }}
          style={{ overflow: 'hidden' }}
        >
          <div className="border-t border-gray-100">
            {!hasBirthdays ? (
              <div className="p-6 text-center">
                <div className="text-gray-500 mb-1 text-sm leading-5">
                  No birthdays in the next 7 days
                </div>
                <div className="text-gray-400 text-xs leading-4">
                  Check back soon! 🎉
                </div>
              </div>
            ) : (
              <div className="p-4 space-y-3">
                {/* Today's birthdays */}
                {todayBirthdays.map((birthday) => (
                  <motion.div
                    key={birthday.id}
                    className="flex items-center justify-between p-3 bg-pink-50 rounded-lg border border-pink-200"
                    whileHover={{ scale: 1.02 }}
                  >
                    <div className="flex items-center gap-3">
                      <Gift className="w-5 h-5 text-pink-500" />
                      <div>
                        <div className="font-medium text-gray-900 text-sm leading-5">
                          {birthday.firstName} {birthday.lastName}
                        </div>
                        <div className="text-gray-600 capitalize text-xs leading-4">
                          {birthday.type} • Today!
                        </div>
                      </div>
                    </div>
                    <div className="text-2xl">🎉</div>
                  </motion.div>
                ))}

                {/* Upcoming birthdays */}
                {upcomingBirthdays.map((birthday) => {
                  const bYMD = getYMDString(birthday.date);
                  const bDate = new Date(bYMD + "T00:00:00");
                  const tDate = new Date(todayStr + "T00:00:00");
                  const diff = Math.round((bDate - tDate) / (1000 * 60 * 60 * 24));
                  
                  return (
                    <motion.div
                      key={birthday.id}
                      className="flex items-center justify-between p-3 bg-purple-50 rounded-lg border border-purple-200"
                      whileHover={{ scale: 1.02 }}
                    >
                      <div className="flex items-center gap-3">
                        <Cake className="w-5 h-5 text-purple-500" />
                        <div>
                          <div className="font-medium text-gray-900 text-sm leading-5">
                            {birthday.firstName} {birthday.lastName}
                          </div>
                          <div className="text-gray-600 capitalize text-xs leading-4">
                            {birthday.type} • In {diff} day{diff > 1 ? 's' : ''}
                          </div>
                        </div>
                      </div>
                      <div className="text-xl">🎂</div>
                    </motion.div>
                  );
                })}
              </div>
            )}

            {/* Celebration footer - only show when expanded */}
            <div className="px-4 pb-4 pt-2 border-t border-gray-100">
              <div className="text-center text-gray-500 text-xs leading-4">
                ✨ Make their day special with a personal message! ✨
              </div>
            </div>
          </div>
        </motion.div>
      </MaterialCard>
    </motion.div>
  );
};